<template>
  <div>
    <b-breadcrumb :items="items"></b-breadcrumb>

    <b-row class="mx-0 mb-3">
      <b-col>
        <b-card class="shadow bg-light">
          <b-card-title title-tag="h6"
            >Gestion des relevés de compte</b-card-title
          >
          <div class="mb-3">
            <b-button
              :variant="busy ? 'warning' : 'success'"
              :disabled="busy || nSelected == 0"
              @click="print_releves()"
            >
              <span v-if="busy"
                >Chargement des relevés... ({{ creditors.records.length }})
                <b-icon icon="arrow-clockwise" animation="spin"></b-icon>
                <b-progress variant="success" :max="100" show-progress animated>
                  <b-progress-bar :value="creditors.percentage"
                    >{{ creditors.percentage }} %</b-progress-bar
                  >
                </b-progress>
              </span>
              <span v-else-if="nSelected > 0"
                >Prévisualiser {{ nSelected }} relevés sur
                {{ creditors.records.length }}
                <b-icon-check-circle-fill></b-icon-check-circle-fill
              ></span>
              <span v-else> Aucun relevé sélectionné </span>
            </b-button>
            <b-button v-if="!busy" @click="load(false)" class="ml-3"
              >Recharger les relevés</b-button
            >
            <b-button
              v-if="!busy && nSelected > 0"
              @click="unselectAll()"
              class="ml-3"
              >Désélectionner tous les relevés</b-button
            >
            <b-button
              v-else-if="!busy && nSelected == 0"
              @click="selectAll()"
              class="ml-3"
            >
              Sélectionner tous les relevés
            </b-button>
          </div>
          <div class="mb-3">
            <label>Recherche d'un créancier</label>
            <b-input debounce="400" v-model="filter"></b-input>
          </div>

          <template v-for="(creditor, index) in filteredCreditors">
            <b-card :key="creditor.ID" class="mb-2 shadow bg-light">
              <b-card-title title-tag="h6">
                <span class="text-dark">#{{ index }}</span>
                <router-link
                  class="ml-3"
                  target="_blank"
                  :to="{
                    name: 'creditorView',
                    params: { id: creditor.ID },
                  }"
                  >{{ creditor.creancier }} / {{ creditor.ID }}</router-link
                >
                <input
                  style="
                    transform: scale(1.5);
                    padding: 10px;
                    margin-right: 5px;
                  "
                  type="checkbox"
                  :id="'check' + creditor.ID"
                  @click="toggle_selection(index)"
                  :checked="creditor.selected"
                  class="ml-3"
                />
                <label :for="'check' + creditor.ID" class="ml-2 text-dark"
                  >Confirmer la génération du relevé de compte</label
                >
              </b-card-title>
              <b-row>
                <b-col lg="12" v-if="creditor.alert">
                  <b-alert show variant="danger">
                    {{ creditor.alert }}
                  </b-alert>
                </b-col>
                <b-col lg="4">
                  <b-card
                    title="Flux financier"
                    title-tag="h6"
                    class="mb-2 shadow bg-light"
                  >
                    <b-table-simple hover small striped responsive caption-top>
                      <b-tbody>
                        <b-tr>
                          <b-td> Précédent rapport d'activité </b-td>
                          <b-td class="text-right">
                            {{ creditor.solde_activite }} €
                          </b-td>
                        </b-tr>
                        <b-tr>
                          <b-td> Total dû par le créancier </b-td>
                          <b-td class="text-right">
                            {{ creditor.solde_creancier }} €
                          </b-td>
                        </b-tr>
                        <b-tr>
                          <b-td> Total perçu chez nous </b-td>
                          <b-td class="text-right">
                            {{ creditor.chez_nous }} €
                          </b-td>
                        </b-tr>
                        <b-tr>
                          <b-td> Total perçu chez vous </b-td>
                          <b-td class="text-right">
                            {{ creditor.chez_vous }} €
                          </b-td>
                        </b-tr>
                        <b-tr>
                          <b-td>
                            {{
                              creditor.balance > 0
                                ? "Balance en faveur du cabinet"
                                : "Balance en faveur du créancier"
                            }}</b-td
                          >
                          <b-td class="text-right" v-if="creditor.balance >= 0">
                            <strong class="text-danger"
                              >{{ creditor.balance }} €</strong
                            >
                          </b-td>
                          <b-td class="text-right" v-else>
                            <strong class="text-success"
                              >{{ creditor.balance.substr(1) }} €</strong
                            >
                          </b-td>
                        </b-tr>
                        <b-tr>
                          <b-td>Numéro de compte du créancier</b-td>
                          <b-td
                            class="text-right"
                            style="font-family: monospace"
                            ><strong>{{
                              creditor.compte_creancier
                            }}</strong></b-td
                          >
                        </b-tr>
                      </b-tbody>
                    </b-table-simple>
                  </b-card>
                  <b-card
                    title="Informations"
                    title-tag="h6"
                    class="mb-2 shadow bg-light"
                  >
                    <b-row>
                      <b-col lg="12" v-if="creditor.note_releve !== ''">
                        <strong
                          ><i
                            class="fad fa-exclamation-triangle text-danger mr-3"
                          ></i
                          >Note de relevé : {{ creditor.note_releve }}</strong
                        >
                      </b-col>
                      <b-col lg="12" v-if="creditor.note_releve !== ''">
                        <hr />
                      </b-col>
                      <b-col lg="12">
                        <span
                          class="mr-3"
                          v-html="iconCheck(creditor.virement)"
                        ></span
                        >Demande les versements par virement
                      </b-col>
                      <b-col lg="12">
                        <span
                          class="mr-3"
                          v-html="iconCheck(creditor.regularisation)"
                        ></span
                        >Accepte la régularisation via le relevé de compte
                      </b-col>
                    </b-row>
                  </b-card>
                </b-col>
                <b-col lg="8">
                  <b-card
                    v-if="creditor.factures.length > 0"
                    title="Factures en attente de relevé de compte"
                    title-tag="h6"
                    class="mb-2 shadow bg-light"
                  >
                    <b-table
                      striped
                      hover
                      small
                      :items="creditor.factures"
                      :fields="facturesFields"
                    >
                      <template v-slot:cell(ID)="data">
                        <router-link
                          target="_blank"
                          :to="{
                            name: 'invoiceView',
                            params: { id: data.item.ID },
                          }"
                          >{{ data.item.ID }}</router-link
                        >
                      </template>
                      <template v-slot:cell(releve_compteID)="data">
                        <span v-if="data.item.releve_compteID != 0"
                          ><router-link
                            target="_blank"
                            :to="{
                              name: 'accountStatement',
                              params: { id: data.item.releve_compteID },
                            }"
                            >{{ data.item.releve_compteID }}</router-link
                          ><i
                            class="fad fa-exclamation-triangle text-danger ml-2"
                          ></i
                        ></span>
                        <span v-else>--</span>
                      </template>
                      <template v-slot:bottom-row>
                        <td />
                        <td />
                        <td />
                        <td />
                        <td class="text-right">
                          <strong>Total</strong>
                        </td>
                        <td>
                          <strong>
                            <span class="float-right"
                              >{{
                                creditor.factures
                                  .reduce(
                                    (a, b) => a + parseFloat(b.montant),
                                    0
                                  )
                                  .toLocaleString(undefined, {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                  })
                              }}
                              €</span
                            >
                          </strong>
                        </td>
                        <td>
                          <strong>
                            <span class="float-right"
                              >{{
                                creditor.factures
                                  .reduce((a, b) => a + parseFloat(b.solde), 0)
                                  .toLocaleString(undefined, {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                  })
                              }}
                              €</span
                            >
                          </strong>
                        </td>
                      </template>
                    </b-table>
                  </b-card>
                  <b-card
                    title="Paiements créanciers en attente du relevé de compte"
                    title-tag="h6"
                    class="mb-2 shadow bg-light"
                    v-if="creditor.factures_paiements.length > 0"
                  >
                    <b-table
                      striped
                      hover
                      small
                      :items="creditor.factures_paiements"
                      :fields="facturesPaiementsFields"
                    >
                      <template v-slot:cell(releve_compteID)="data">
                        <span v-if="data.item.releve_compteID != 0"
                          ><router-link
                            target="_blank"
                            :to="{
                              name: 'accountStatement',
                              params: { id: data.item.releve_compteID },
                            }"
                            >{{ data.item.releve_compteID }}</router-link
                          ><i
                            class="fad fa-exclamation-triangle text-danger ml-2"
                          ></i
                        ></span>
                        <span v-else>--</span>
                      </template>
                      <template v-slot:bottom-row>
                        <td />
                        <td />
                        <td class="text-right">
                          <strong>Total</strong>
                        </td>
                        <td>
                          <strong>
                            <span class="float-right"
                              >{{
                                creditor.factures_paiements
                                  .reduce(
                                    (a, b) => a + parseFloat(b.montant),
                                    0
                                  )
                                  .toLocaleString(undefined, {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                  })
                              }}
                              €</span
                            >
                          </strong>
                        </td>
                      </template>
                    </b-table>
                  </b-card>
                  <b-card
                    title="Paiements débiteurs liés"
                    title-tag="h6"
                    class="mb-2 shadow bg-light"
                    v-if="creditor.paiements.length > 0"
                  >
                    <b-table
                      striped
                      small
                      hover
                      :items="creditor.paiements"
                      :fields="paiementsFields"
                    >
                      <template v-slot:cell(mandatID)="data">
                        <router-link
                          target="_blank"
                          :to="{
                            name: 'warrantView',
                            params: { id: data.item.mandatID },
                          }"
                          >{{ data.item.mandatID }}</router-link
                        >
                      </template>
                      <template v-slot:cell(factureID)="data">
                        <router-link
                          target="_blank"
                          :to="{
                            name: 'invoiceView',
                            params: { id: data.item.factureID },
                          }"
                          >{{ data.item.factureID }}</router-link
                        >
                      </template>
                      <template v-slot:cell(releve_compteID)="data">
                        <span v-if="data.item.releve_compteID">
                          <router-link
                            target="_blank"
                            :to="{
                              name: 'accountStatement',
                              params: { id: data.item.releve_compteID },
                            }"
                            >{{ data.item.releve_compteID }}</router-link
                          >
                          <i
                            class="fad fa-exclamation-triangle text-danger ml-2"
                          ></i
                        ></span>
                        <span v-else>--</span>
                      </template>
                      <template v-slot:bottom-row>
                        <td />
                        <td />
                        <td />
                        <td />
                        <td class="text-right">
                          <strong>Total</strong>
                        </td>
                        <td>
                          <strong>
                            <span class="float-right"
                              >{{
                                creditor.paiements
                                  .reduce((a, b) => {
                                    if (b.reception !== "creancier") {
                                      return a + parseFloat(b.montant);
                                    }
                                    return a;
                                  }, 0)
                                  .toLocaleString(undefined, {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                  })
                              }}
                              €</span
                            >
                          </strong>
                        </td>
                        <td>
                          <strong>
                            <span class="float-right"
                              >{{
                                creditor.paiements
                                  .reduce((a, b) => {
                                    if (b.reception == "creancier") {
                                      return a + parseFloat(b.montant);
                                    }
                                    return a;
                                  }, 0)
                                  .toLocaleString(undefined, {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                  })
                              }}
                              €</span
                            >
                          </strong>
                        </td>
                      </template>
                    </b-table>
                  </b-card>
                </b-col>
              </b-row>
            </b-card>
          </template>

          <div v-if="busy">Chargement...</div>
          <div v-else>
            <b-button
              :variant="busy ? 'warning' : 'success'"
              :disabled="busy"
              @click="print_releves()"
              class="mb-3"
            >
              <span v-if="busy"
                >Chargement des relevés...
                <b-icon icon="arrow-clockwise" animation="spin"></b-icon
              ></span>
              <span v-else
                >Prévisualiser les relevés
                <b-icon-check-circle-fill></b-icon-check-circle-fill
              ></span>
            </b-button>
          </div>
        </b-card>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import DateTime from "@/services/DateTime.js";
import account_statements from "@/services/account-statements.js";
export default {
  name: "manageAccountStatements",
  components: {},
  props: {
    creancierID: {
      type: [Number, String],
      default: null,
    },
  },
  data() {
    return {
      creditors: {
        records: [],
      },
      items: [
        {
          text: "Gestion",
          to: { name: "home" },
        },
        {
          text: "Gestion des relevés de compte",
          active: true,
        },
      ],
      loaded: false,
      offset: 0,
      busy: true,
      steps: 20,
      filter: "",
      paiementsFields: [
        {
          key: "factureID",
          label: "Facture",
          sortable: true,
          thStyle: "width:10%",
          class: "text-center",
        },
        {
          key: "releve_compteID",
          label: "Relevé précédent",
          class: "text-center",
          thStyle: "width:10%",
        },
        {
          key: "date",
          label: "Date",
          formatter: (value) => this.toDate(value),
          thStyle: "width:20%",
          class: "text-center",
        },
        {
          key: "mandatID",
          label: "Mandat",
          class: "text-center",
          thStyle: "width:30%",
        },
        {
          key: "type",
          label: "Type",
          class: "text-center",
        },
        {
          key: "chez_nous",
          label: "Chez nous",
          class: "text-right",
          formatter: (value, key, item) => {
            if (item.reception !== "creancier") {
              return this.twoDigits(item.montant) + " €";
            }
            return "";
          },
        },
        {
          key: "chez_vous",
          label: "Chez vous",
          class: "text-right",
          formatter: (value, key, item) => {
            if (item.reception == "creancier") {
              return this.twoDigits(item.montant) + " €";
            }
            return "";
          },
        },
      ],
      facturesPaiementsFields: [
        {
          key: "factureID",
          label: "Facture",
          sortable: true,
          thStyle: "width:10%",
          class: "text-center",
        },
        {
          key: "releve_compteID",
          label: "Relevé précédent",
          thStyle: "width:10%",
          class: "text-center",
        },
        {
          key: "date",
          label: "Date de paiement",
          formatter: (value) => this.toDate(value),
          class: "text-center",
        },
        {
          key: "montant",
          label: "Montant",
          class: "text-right",
          formatter: (value) => this.twoDigits(value) + " €",
        },
      ],
      facturesFields: [
        {
          key: "ID",
          label: "#",
          sortable: true,
          thStyle: "width:10%",
          class: "text-center",
        },
        {
          key: "releve_compteID",
          label: "Relevé précédent",
          thStyle: "width:10%",
          class: "text-center",
        },
        {
          key: "date",
          label: "Date",
          formatter: (value) => this.toDate(value),
          thStyle: "width:20%",
          class: "text-center",
        },
        {
          key: "date_echeance",
          label: "Échéance",
          formatter: (value) => this.toDate(value),
          thStyle: "width:30%",
          class: "text-center",
        },
        {
          key: "type",
          label: "Type",
          class: "text-center",
        },
        {
          key: "montant",
          label: "Montant",
          formatter: (value) => this.twoDigits(value) + " €",
          class: "text-right",
        },
        {
          key: "solde",
          label: "Solde",
          formatter: (value) => this.twoDigits(value) + " €",
          class: "text-right",
        },
      ],
    };
  },
  created() {
    this.load(!this.creancierID ? true : false);
  },
  beforeRouteLeave(to, from, next) {
    if (!this.busy && this.loaded) {
      this.$store.commit("updateAccountStatements", this.creditors.records);
    }
    next();
  },
  computed: {
    filteredCreditors() {
      return this.creditors.records.filter(
        (x) =>
          x.creancier.toUpperCase().indexOf(this.filter.toUpperCase()) !== -1
      ); //.slice(0, this.steps);
    },
    nSelected() {
      return this.creditors.records.filter((x) => x.selected).length;
    },
  },
  methods: {
    toggle_selection(index) {
      this.creditors.records[index].selected =
        !this.creditors.records[index].selected;
    },
    selectAll() {
      this.creditors.records.forEach((el) => (el.selected = true));
    },
    unselectAll() {
      this.creditors.records.forEach((el) => (el.selected = false));
    },
    print_releves() {
      let ids = this.creditors.records
        .filter((x) => x.selected)
        .map((x) => x.ID);
      this.$router.push({
        name: "previewAccountStatements",
        params: { creditors: ids },
      });
    },
    iconCheck(val) {
      switch (val) {
        case "yes":
          return `<i class="fad fa-check text-success"></i>`;
        case "no":
          return `<i class="fad fa-times text-danger"></i>`;
      }
    },
    load(cache = true) {
      if (cache && this.$store.getters.account_statements.length > 0) {
        this.creditors.records = this.$store.getters.account_statements;
        this.loaded = true;
        this.busy = false;
      } else {
        this.creditors.records = [];
        this.busy = true;
        this.loaded = false;
        account_statements.manage(this.creancierID).then((response) => {
          this.creditors = response.data;
          if (this.creditors.continue) {
            this.offset = this.creditors.offset;
            this.continue();
          } else {
            this.busy = false;
          }
          this.loaded = true;
        });
      }
    },
    continue() {
      account_statements
        .manage(this.creancierID, this.offset)
        .then((response) => {
          this.creditors.records = [
            ...this.creditors.records,
            ...response.data.records,
          ];
          this.creditors.percentage = response.data.percentage;
          if (response.data.continue) {
            this.offset = response.data.offset;
            this.continue();
          } else {
            this.$store.commit(
              "updateAccountStatements",
              this.creditors.records
            );
            this.busy = false;
          }
          this.loaded = true;
        });
    },

    toDate(d) {
      return DateTime.humanDate(d);
    },
    twoDigits(value) {
      return Number(value).toFixed(2);
    },
  },
};
</script>

<style lang="scss" scoped>
.pre-formatted {
  white-space: pre;
}
</style>